<script setup lang="ts">
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { computed, ref, defineEmits } from 'vue'
  import {
    AiAugmentedByMapperKeysEnum as DatamapperKey,
    type CustomerCase as Case
  } from '@/types/generated'
  import InputField from '@/components/admin/form/InputField.vue'
  import Button from '@/components/ui/button/Button.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { Loader2 } from 'lucide-vue-next'
  import { useToast } from '@/components/ui/toast'
  import { storeToRefs } from 'pinia'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'

  const emit = defineEmits(['close'])

  // Translations
  const { t } = useTranslation({
    en: {
      vehicleData: 'Vehicle Data',
      vehicleAge: 'Vehicle Age',
      months: 'Months',
      mileage: 'Mileage',
      km: 'km',
      netRepairCosts: 'Net Repair Costs',
      depreciation: 'Depreciation',
      bremerFormula: 'Bremer Formula',
      hamburgerModel: 'Hamburger Model',
      averageDepreciation: 'Depreciation (averaged)',
      save: 'Save',
      updatedFields: 'Updated Fields'
    },
    de: {
      vehicleData: 'Fahrzeugdaten',
      vehicleAge: 'Fahrzeugalter',
      months: 'Monate',
      mileage: 'Kilometerstand',
      km: 'km',
      netRepairCosts: 'Nettoreparaturkosten',
      depreciation: 'Wertminderung',
      bremerFormula: 'Wertminderung (Bremer Formel)',
      hamburgerModel: 'Wertminderung (Hamburger Modell)',
      averageDepreciation: 'Wertminderung (gemittelt)',
      save: 'Speichern',
      updatedFields: 'Felder aktualisiert'
    }
  })

  const caseStore = useCaseStore()
  const caseRecord = computed<Case>(() => caseStore.caseRecord as Case)
  const { isSavingCase } = storeToRefs(caseStore)
  const { trackEvent } = useMetrics()

  // Input fields with store bindings
  const vehicleAge = ref<number>(0)
  const mileage = ref<number>(0)
  const repairCosts = ref<number>(0)

  // Load initial data
  const loadInitialData = async () => {
    // Load numeric values
    const vehicleAgeValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_VEHICLE_AGE_MONTHS
    )
    vehicleAge.value = vehicleAgeValue ? parseFloat(vehicleAgeValue) : 0

    const mileageValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_VEHICLE_MILEAGE
    )
    mileage.value = mileageValue ? parseFloat(mileageValue) : 0

    const repairCostsValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_REPAIR_COSTS_WITHOUT_VAT
    )
    repairCosts.value = repairCostsValue ? parseFloat(repairCostsValue) : 0
  }

  // Call loadInitialData when component is mounted
  loadInitialData()

  // Helper function to format numbers
  const formatToTwoDecimals = (value: number): number => {
    return Number((value || 0).toFixed(2))
  }

  // Save data to backend
  const { toast } = useToast()
  const handleSaveAll = async () => {
    await caseStore.bulkUpdateDataWithMapperKeys({
      [DatamapperKey.LAW_TRAFFIC_LAW_VEHICLE_AGE_MONTHS]: vehicleAge.value,
      [DatamapperKey.LAW_TRAFFIC_LAW_VEHICLE_MILEAGE]: mileage.value,
      [DatamapperKey.LAW_TRAFFIC_LAW_REPAIR_COSTS_WITHOUT_VAT]: repairCosts.value,
      [DatamapperKey.LAW_TRAFFIC_LAW_MERANTILE_VALUE_LOSS]: averageDevaluation.value
    })

    // Track calculator saved event
    await trackEvent(MetricEventType.CALCULATOR_SAVED, {
      calculator_type: 'Depreciation',
      calculated_sum: averageDevaluation.value,
      vehicle_age_months: vehicleAge.value,
      mileage: mileage.value,
      repair_costs: repairCosts.value,
      bremer_formula_result: bremerFormel.value,
      hamburger_model_result: hamburgerModell.value
    })

    toast({
      title: t('updatedFields'),
      description: 'Depreciation fields updated',
      variant: 'default'
    })
    emit('close')
  }

  // Calculations
  const bremerFormel = computed(() => {
    if (!vehicleAge.value === null || !repairCosts.value === null) return 0

    // Bremer Formel based on vehicle age in months
    const age = Number(vehicleAge.value)
    let factor = 0
    if (age < 7) factor = 0.3
    else if (age < 13) factor = 0.25
    else if (age < 25) factor = 0.2
    else if (age < 37) factor = 0.15
    else if (age < 61) factor = 0.1

    const adjustedRepairCosts = (Number(repairCosts.value) * 2) / 3
    const result = factor * adjustedRepairCosts
    return formatToTwoDecimals(result)
  })

  const hamburgerModell = computed(() => {
    if (!mileage.value === null || !repairCosts.value === null) return 0

    // Hamburger Modell based on mileage
    const km = Number(mileage.value)
    let factor = 0
    if (km <= 20000) factor = 0.3
    else if (km <= 50000) factor = 0.2
    else if (km <= 75000) factor = 0.15
    else if (km <= 100000) factor = 0.1

    const adjustedRepairCosts = (Number(repairCosts.value) * 2) / 3
    const result = factor * adjustedRepairCosts
    return formatToTwoDecimals(result)
  })

  const averageDevaluation = computed(() => {
    if (bremerFormel.value === null || hamburgerModell.value === null) return 0

    const halfHamburger = formatToTwoDecimals(hamburgerModell.value / 2)
    const result = bremerFormel.value + halfHamburger

    return formatToTwoDecimals(result)
  })
</script>

<template>
  <div class="mt-2 flex h-full flex-col overflow-hidden">
    <div class="max-h-[67vh] flex-1 space-y-4 overflow-y-auto">
      <!-- Vehicle Data Section -->
      <div class="space-y-4">
        <div class="grid grid-cols-2 items-center rounded-md bg-gray-50 p-3">
          <h3 class="col-span-2 pb-1 text-lg font-semibold text-gray-900">
            {{ t('vehicleData') }}
          </h3>
          <InputField
            v-model="vehicleAge"
            :label="t('vehicleAge')"
            name="vehicle-age"
            type="number"
            :suffix="t('months')"
            asterisk
            data-cy="depreciationInput-vehicleAge"
          />
          <InputField
            v-model="mileage"
            :label="t('mileage')"
            name="mileage"
            type="number"
            :suffix="t('km')"
            asterisk
            data-cy="depreciationInput-mileage"
          />
          <InputField
            v-model="repairCosts"
            :label="t('netRepairCosts')"
            name="repair-costs"
            type="number"
            suffix="€"
            asterisk
            data-cy="depreciationInput-repairCosts"
          />
        </div>
      </div>

      <!-- Depreciation Results Section -->
      <div class="space-y-4">
        <div class="grid grid-cols-2 items-center rounded-md bg-gray-50 p-3">
          <h3 class="col-span-2 pb-1 text-lg font-semibold text-gray-900">
            {{ t('depreciation') }}
          </h3>
          <InputField
            v-model="bremerFormel"
            :label="t('bremerFormula')"
            name="bremer-formel"
            :disabled="true"
            suffix="€"
            data-cy="depreciationResult-bremerFormula"
          />
          <InputField
            v-model="hamburgerModell"
            :label="t('hamburgerModel')"
            name="hamburger-modell"
            :disabled="true"
            suffix="€"
            data-cy="depreciationResult-hamburgerModel"
          />
        </div>
      </div>
    </div>

    <div class="mt-4">
      <div class="flex items-end gap-4 px-1 pb-2">
        <div class="flex-grow">
          <InputField
            v-model="averageDevaluation"
            name="average-devaluation"
            :label="t('averageDepreciation')"
            :disabled="true"
            class="font-medium text-green-600"
            suffix="€"
            data-cy="depreciationResult-averageDepreciation"
          />
        </div>
        <Button
          :class="[
            'inline-flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-white',
            'focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2',
            isSavingCase ? 'cursor-not-allowed bg-purple-400' : 'bg-purple-600 hover:bg-purple-700',
            'disabled:cursor-not-allowed disabled:opacity-50'
          ]"
          :disabled="isSavingCase"
          data-cy="depreciationSaveBtn"
          @click="handleSaveAll"
        >
          <span v-if="isSavingCase">
            <Loader2 class="mr-1 inline h-4 w-4 animate-spin align-text-bottom" />
            {{ t('save') }}
          </span>
          <span v-else>
            {{ t('save') }}
          </span>
        </Button>
      </div>
    </div>
  </div>
</template>
